// Lien filing checklist — Notice of Intent and Mechanic's Lien, tracked per invoice (date, who, county, recording #).
// Each step takes document uploads; the parser pre-fills whatever the paperwork states.
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { updateInvoice, getLienDocs, uploadLienDoc, deleteLienDoc, lienDocUrl } from "@/lib/api";
import { shortDate, money } from "@/lib/format";

const EMPTY = { filed: false, filed_on: "", filed_by: "", county: "", reference: "", notes: "" };
const errMsg = (e) => e?.response?.data?.detail || e?.message || "Failed";
const today = () => new Date().toISOString().slice(0, 10);
const DOC_TYPE = { notice_of_intent: "Notice of Intent", lien_statement: "Lien Statement", recorded_lien: "Recorded Lien", preliminary_notice: "Preliminary Notice", release: "Release", other: "Document" };

// Fields the parser found that the step can hold — only blanks are filled, nothing typed is overwritten.
function applyExtracted(step, x) {
  const next = { ...step };
  ["filed_on", "filed_by", "county", "reference"].forEach((k) => { if (!next[k] && x[k]) next[k] = x[k]; });
  const extra = [x.doc_type && DOC_TYPE[x.doc_type], x.owner && `Owner: ${x.owner}`, x.property_address && `Property: ${x.property_address}`,
    x.amount_claimed != null && `Amount claimed ${money(x.amount_claimed)}`, x.last_work_date && `Last work ${shortDate(x.last_work_date)}`, x.notes].filter(Boolean).join(" · ");
  if (extra && !(next.notes || "").includes(extra)) next.notes = [next.notes, extra].filter(Boolean).join("\n");
  if (next.filed_on && !next.filed) next.filed = true;
  return next;
}

function DocRow({ invoiceId, d, onDelete }) {
  const x = d.extracted || {};
  const summary = [x.doc_type && DOC_TYPE[x.doc_type], x.filed_on && shortDate(x.filed_on), x.county && `${x.county} County`, x.reference && `#${x.reference}`].filter(Boolean).join(" · ");
  return (
    <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5" data-testid={`lien-doc-${d.id}`}>
      <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
      <div className="min-w-0 flex-1">
        <a href={lienDocUrl(invoiceId, d.id, true)} target="_blank" rel="noreferrer" className="block truncate text-xs font-medium text-brand hover:underline" title={d.original_filename} data-testid={`lien-doc-open-${d.id}`}>{d.original_filename}</a>
        <div className="truncate text-[10px] text-slate-500" data-testid={`lien-doc-summary-${d.id}`}>{d.parse_error ? <span className="text-amber-700">{d.parse_error}</span> : summary || "Nothing extracted"} · {d.uploaded_by} {shortDate(d.created_at)}</div>
      </div>
      <button type="button" className="text-slate-400 hover:text-rose-600" title="Remove document" onClick={() => onDelete(d)} data-testid={`lien-doc-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function Step({ id, title, due, dueLabel, step, onChange, by, invoiceId, docs, onDocs }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const set = (patch) => onChange({ ...step, ...patch });
  const toggle = (filed) => set(filed ? { filed, filed_on: step.filed_on || today(), filed_by: step.filed_by || by } : { filed });
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("step", id);
      const { data } = await uploadLienDoc(invoiceId, fd);
      onDocs([data, ...docs]);
      if (data.parse_error) toast.warning(data.parse_error);
      else { onChange(applyExtracted(step, data.extracted || {})); toast.success(`${DOC_TYPE[data.extracted?.doc_type] || "Document"} read — blanks filled in, check and save`); }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const remove = async (d) => {
    try { await deleteLienDoc(invoiceId, d.id); onDocs(docs.filter((x) => x.id !== d.id)); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className={`rounded-lg border p-3 space-y-2 ${step.filed ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200"}`} data-testid={`lien-step-${id}`}>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" className="h-4 w-4 accent-brand" checked={!!step.filed} onChange={(e) => toggle(e.target.checked)} data-testid={`lien-step-${id}-filed`} />
        <span className="text-sm font-semibold text-slate-800">{title}</span>
        <span className="ml-auto text-[11px] text-slate-500 num">{dueLabel} {due ? shortDate(due) : "—"}</span>
      </label>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-[11px]">Filed on</Label>
          <Input type="date" value={step.filed_on || ""} onChange={(e) => set({ filed_on: e.target.value })} className="h-8 text-xs" data-testid={`lien-step-${id}-date`} />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">Filed by</Label>
          <Input value={step.filed_by || ""} onChange={(e) => set({ filed_by: e.target.value })} className="h-8 text-xs" data-testid={`lien-step-${id}-by`} />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">County</Label>
          <Input value={step.county || ""} onChange={(e) => set({ county: e.target.value })} placeholder="Denver" className="h-8 text-xs" data-testid={`lien-step-${id}-county`} />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">Reception / recording #</Label>
          <Input value={step.reference || ""} onChange={(e) => set({ reference: e.target.value })} className="h-8 text-xs" data-testid={`lien-step-${id}-ref`} />
        </div>
      </div>
      <Textarea value={step.notes || ""} onChange={(e) => set({ notes: e.target.value })} rows={2} placeholder="Notes (served by certified mail, copies to…)" className="text-xs" data-testid={`lien-step-${id}-notes`} />
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-medium text-slate-600">Documents</span>
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`lien-step-${id}-file`} />
          <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} title="Upload the filed paperwork — dates, county and recording # are read into the blanks above"
            className="ml-auto inline-flex h-6 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 text-[11px] text-slate-700 hover:border-brand hover:text-brand disabled:opacity-60" data-testid={`lien-step-${id}-upload`}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} {busy ? "Reading…" : "Upload & read"}
          </button>
        </div>
        {docs.length > 0 && <div className="space-y-1">{docs.map((d) => <DocRow key={d.id} invoiceId={invoiceId} d={d} onDelete={remove} />)}</div>}
      </div>
    </div>
  );
}

export function LienChecklistDialog({ row, by, onClose, onSaved }) {
  const [intent, setIntent] = useState({ ...EMPTY, ...(row.lien_filings?.intent || {}) });
  const [lien, setLien] = useState({ ...EMPTY, ...(row.lien_filings?.lien || {}) });
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { getLienDocs(row.id).then((r) => setDocs(r.data || [])).catch(() => {}); }, [row.id]);
  const save = async () => {
    setBusy(true);
    try {
      await updateInvoice(row.id, { lien_filings: { intent, lien } });
      toast.success(lien.filed ? "Lien filing recorded — Lien Status set to Filed" : intent.filed ? "Notice of Intent recorded" : "Checklist saved");
      onSaved?.(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dialog-lien-checklist">
        <DialogHeader>
          <DialogTitle>Lien filing checklist — invoice {row.invoice_number}</DialogTitle>
          <DialogDescription>{row.customer_name} · {row.job_name || row.job_number}. Last day of work {row.last_work_date ? shortDate(row.last_work_date) : "not set"} · enforce by {row.enforce_date ? shortDate(row.enforce_date) : "—"}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Step id="intent" title="Notice of Intent to Lien" due={row.intent_due} dueLabel="due" step={intent} onChange={setIntent} by={by}
            invoiceId={row.id} docs={docs.filter((d) => d.step === "intent")} onDocs={(next) => setDocs([...next, ...docs.filter((d) => d.step !== "intent")])} />
          <Step id="lien" title="Mechanic's Lien" due={row.lien_date} dueLabel="file by" step={lien} onChange={setLien} by={by}
            invoiceId={row.id} docs={docs.filter((d) => d.step === "lien")} onDocs={(next) => setDocs([...docs.filter((d) => d.step !== "lien"), ...next])} />
          <div className="text-[11px] text-slate-500">Checking the lien step sets Lien Status to Filed and clears the lien watch; each filing is also logged in the invoice notes. Uploaded paperwork stays with the invoice.</div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="lien-checklist-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="lien-checklist-save">{busy ? "Saving…" : "Save checklist"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
